import {ExpeditionId} from "@webmate-web-core/shared/types/ids";
import {WebSocketService} from "@webmate-web-core/shared/services/events/websocket.service";
import {ConsoleConfig} from "../interfaces/console-config.interface";
import {AuthService} from "@webmate-web-core/shared/services/auth.service";
import { HttpClient } from "@angular/common/http";
import {WebmateOptions} from "@webmate-web-core/webmate-options";
import * as protocol from './protocol.json';
// import api from './api.js';
import {Observable, of, Subject, switchMap, throwError} from "rxjs";
import {map} from "rxjs/operators";

export type CDPSessionId = string;

export class CDPClient {
    private nextCommandId = 0;
    private ws: WebSocket | null = null;

    private messageHandlers = new Map<number /* messageId */, Subject<any>>();

    private pageUrl = new Subject<string>();

    getPageUrlChanges(): Observable<string> {
        return this.pageUrl.asObservable();
    }

    static create(expeditionId: ExpeditionId, authService: AuthService): Observable<CDPClient> {
        const cdpClient = new CDPClient(expeditionId, authService);
        try {
            return cdpClient.connect().pipe(map(() => cdpClient));
        } catch (e) {
            return throwError(e);
        }
    }

    constructor(private expeditionId: ExpeditionId, public authService: AuthService) {}

    createSessionForFirstPage(): Observable<CDPSessionId> {
        return this.sendCommand('Target.getTargets').pipe(
            map((response) => response.targetInfos.filter((targetInfo: any) => targetInfo.type === 'page')),
            switchMap(targetInfos => {
                let targetId: string;
                if (targetInfos.length === 0) {
                    return throwError(() => 'No page target available.');
                } else if (targetInfos.length > 1) {
                    console.warn('More than one page target available. Using first one.');
                }
                targetId = targetInfos[0].targetId;
                return this.sendCommand('Target.attachToTarget', {targetId: targetId, flatten: true}).pipe(map(result => result.sessionId));
            })
        );
    }

    disconnect() {
        this.ws!.close();
    }

    private connect(): Observable<void> {
        // open websocket connection for expedition
        const url = this.authService.makeAuthorizedWebSocketUrl(`/browsersession/${this.expeditionId}/cdp`);
        this.ws = new WebSocket(url);
        // api.prepare(this, protocol);

        this.ws.onmessage = (msg: MessageEvent<any>) => {
            const message = JSON.parse(msg.data);
            this.handleWsMessage(message);
        };

        return Observable.create((observer: any) => {
            if (!this.ws) {
                console.error('no websocket connection');
                observer.error('no websocket connection');
                return;
            }

            this.ws.onopen = () => {
                observer.next();
            }
            this.ws.onclose = () => {
                observer.complete();
            }
            this.ws.onerror = (err) => {
                observer.error(err);
            }
        });
    }

    private handleWsMessage(message: any) {
        if (message.id !== undefined) {
            const handler = this.messageHandlers.get(message.id);
            if (handler) {
                handler.next(message.result);
                handler.complete(); // command handlers are one-shot
            } else {
                console.debug('ignoring message. no handler for id', message);
            }
        }else if (message.method && message.method === "Page.frameNavigated" && message.params.frame.domainAndRegistry !== ""){
            this.pageUrl.next(message.params.frame.url);
        } else {
            // strange.. ignore
            console.debug('ignoring message. has no id nor method', message);
        }
    }

    // send a command to the remote endpoint and register a callback for the reply
    private enqueueCommand(method: string, params: any, sessionId?: CDPSessionId): number /* messageId */ {
        if (!this.ws) {
            throw new Error('Cannot enqueue CDP command: no websocket connection');
        }
        const id = this.nextCommandId++;
        const message = {
            id,
            method,
            sessionId,
            params: params || {}
        };

        this.messageHandlers.set(id, new Subject<any>());
        this.ws.send(JSON.stringify(message));
        return id;
    }


    sendCommand(method: string, params?: any, sessionId?: CDPSessionId): Observable<any> {
        const messageId = this.enqueueCommand(method, params, sessionId);
        if (this.messageHandlers.has(messageId)) {
            return this.messageHandlers.get(messageId).asObservable();
        } else {
            return throwError(`Send command failed. No handler for message id ${messageId}`);
        }
    }
}
